import React from 'react';
import classnames from 'classnames';
import {FormattedMessage} from 'react-intl';
import {format, parseISO} from 'date-fns';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import ButtonAction from '../../../../common/components/ui/ButtonAction';
import {calcLineTotals, Format} from '../../../../common/utils';
import {InvoiceOrderDetails} from '../../types';
import styles from './DetailsPanel.module.scss';
import {VAT} from '../../../../services';

export interface DetailsPanelProps extends InvoiceOrderDetails {
  _id: string;
}

const DetailsPanel = ({
                        createdAt,
                        customerName,
                        contact,
                        idNumber,
                        phone,
                        address,
                        emailRecipients,
                        lines,
                      }: DetailsPanelProps) => {
  const stats = calcLineTotals({lines}, new Date(createdAt));
  const {setPopupPanelContent, closePopupPanel} = useGlobalContext();

  const handleClose = () => {
    closePopupPanel();
    setPopupPanelContent(null);
  };

  return (
    <div className={styles.panelContainer}>
      <div className={styles.content}>
        <div className={styles.title}>
          <FormattedMessage id={'invoiceOrders-popup-details-title'} defaultMessage={'Invoice order details'}/>
        </div>
        <div className={styles.details}>
          <div className={styles.detailsList}>
            <div className={styles.detailsListRow}>
              <div className={styles.detailsListCol}>
                <FormattedMessage id={'invoiceOrders-popup-details-createdAt'} defaultMessage={'Created at:'}/>
              </div>
              <div className={styles.detailsListCol}>
                {format(parseISO(createdAt), 'yyyy-MM-dd')}
              </div>
            </div>
            <div className={styles.detailsListRow}>
              <div className={styles.detailsListCol}>
                <FormattedMessage id={'invoiceOrders-popup-details-customer'} defaultMessage={'Customer:'}/>
              </div>
              <div className={styles.detailsListCol}>
                {customerName}
              </div>
            </div>
            <div className={styles.detailsListRow}>
              <div className={styles.detailsListCol}>
                <FormattedMessage id={'invoiceOrders-popup-details-contact'} defaultMessage={'Contact:'}/>
              </div>
              <div className={styles.detailsListCol}>
                {contact}
              </div>
            </div>
            <div className={styles.detailsListRow}>
              <div className={styles.detailsListCol}>
                <FormattedMessage id={'invoiceOrders-popup-details-idNumber'} defaultMessage={'ID number:'}/>
              </div>
              <div className={styles.detailsListCol}>
                {idNumber}
              </div>
            </div>
            <div className={styles.detailsListRow}>
              <div className={styles.detailsListCol}>
                <FormattedMessage id={'invoiceOrders-popup-details-phone'} defaultMessage={'Phone:'}/>
              </div>
              <div className={styles.detailsListCol}>
                {phone}
              </div>
            </div>
            <div className={styles.detailsListRow}>
              <div className={styles.detailsListCol}>
                <FormattedMessage id={'invoiceOrders-popup-details-address'} defaultMessage={'Address:'}/>
              </div>
              <div className={styles.detailsListCol}>
                {address}
              </div>
            </div>
            <div className={styles.detailsListRow}>
              <div className={styles.detailsListCol}>
                <FormattedMessage id={'invoiceOrders-popup-details-emails'} defaultMessage={'Emails:'}/>
              </div>
              <div className={styles.detailsListCol}>
                {emailRecipients}
              </div>
            </div>
          </div>
          <div className={styles.linesList}>
            {lines.map((line, index) => {
              return (
                <div key={index}>
                  <div className={styles.outlinedItem}>
                    {line.description}
                  </div>
                  <div className={classnames(styles.outlinedItem, styles.verticalCentered)}>
                    <div className={styles.pair}>
                    <span>
                      <FormattedMessage id={'invoiceOrders-popup-details-col-amount'} defaultMessage={'Amount'}/>
                    </span>
                      {line.vatIncluded && (
                        <span>
                          <FormattedMessage
                            id={'invoiceOrders-popup-details-col-vatIncluded'}
                            defaultMessage={'VAT included'}
                          />
                        </span>
                      )}
                    </div>
                    <div className={styles.bigFont}>
                      {line.amount}
                    </div>
                  </div>
                </div>
              );
            })}
            <div
              className={classnames(styles.outlinedItem, styles.verticalCentered, styles.totals)}
            >
              <div className={classnames(styles.detailsListRow, styles.wider)}>
                <div className={styles.detailsListCol}>
                  <FormattedMessage id={'invoiceOrders-popup-details-total'} defaultMessage={'Total'}/>
                </div>
                <div className={styles.detailsListCol}>
                  {Format.displayMonetaryAmount(stats.total, true)}
                </div>
              </div>
              <div className={classnames(styles.detailsListRow, styles.wider)}>
                <div className={styles.detailsListCol}>
                  <FormattedMessage
                    id={'invoiceOrders-popup-details-VAT'}
                    defaultMessage={'VAT'}
                    values={{vat: VAT.percents(new Date(createdAt))}}
                  />
                </div>
                <div className={styles.detailsListCol}>
                  {Format.displayMonetaryAmount(stats.VAT, true)}
                </div>
              </div>
              <div className={classnames(styles.detailsListRow, styles.wider)}>
                <div className={styles.detailsListCol}>
                  <FormattedMessage id={'invoiceOrders-popup-details-totalVAT'} defaultMessage={'Total + VAT'}/>
                </div>
                <div className={styles.detailsListCol}>
                  {Format.displayMonetaryAmount(stats.totalVAT, true)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.buttonContainer}>
        <ButtonAction
          label={(
            <FormattedMessage id={'invoiceOrders-popup-details-closeBtn'} defaultMessage={'Close'}/>
          )}
          onClick={handleClose}
          customClassnames={{
            container: styles.closeButtonContainer,
            label: styles.closeButtonLabel
          }}
        />
      </div>
    </div>
  );
};

export default DetailsPanel;
